import { NavLink, useLocation } from "react-router";
import { cn } from "@/lib/utils";
import { TenantSwitcher } from "@/components/tenant-switcher";
import { usePersona } from "@/providers/persona-context";
import { isNavVisibleForPersona } from "@/components/persona/persona-nav-config";
import { useUnreadCount } from "@/hooks/use-unread-count";
import { useOverdueTaskCount } from "@/hooks/use-overdue-task-count";
import { normalizeFeatureUsageKey, trackFeatureUsage } from "@/hooks/use-intelligent-density";
import { useFeatureReveal } from "@/hooks/use-feature-reveal";
import { useTerminology } from "@/lib/terminology";
import type { LucideIcon } from "lucide-react";
import {
  LayoutDashboard, Users, UserCircle, Handshake, CheckSquare,
  Mail, Send, ListFilter, RefreshCw, FileText, Globe, FormInput,
  Image, Palette, Zap, Bot, ScrollText, Brain, BarChart3, Gauge, Settings,
  Inbox, CalendarCheck, CalendarClock, Truck, Video, Star, Newspaper, MessageCircle, BellRing,
  GraduationCap, Brain as BrainIcon, UsersRound, Route,
  MessageSquare, Building2, TrendingUp, Copy, Package, Trophy,
  MousePointerClick, SlidersHorizontal,
} from "lucide-react";

// ---------------------------------------------------------------------------
// Sidebar Navigation Config — Sprint 16 A2: All emojis → Lucide icons
// ---------------------------------------------------------------------------

interface NavItem {
  label: string;
  path: string;
  icon: LucideIcon;
  children?: NavItem[];
  badge?: number;
}

const NAV_ITEMS: NavItem[] = [
  { label: "Dashboard", path: "/dashboard", icon: LayoutDashboard },
  { label: "Inbox", path: "/inbox", icon: Inbox },
  {
    label: "CRM", path: "/crm", icon: Users,
    children: [
      { label: "Today", path: "/crm/today", icon: LayoutDashboard },
      { label: "Contacts", path: "/crm/contacts", icon: UserCircle },
      { label: "Companies", path: "/crm/companies", icon: Building2 },
      { label: "Deals", path: "/crm/deals", icon: Handshake },
      { label: "Tasks", path: "/crm/tasks", icon: CheckSquare },
      { label: "Forecast", path: "/crm/forecast", icon: TrendingUp },
      { label: "Segments", path: "/crm/segments", icon: ListFilter },
      { label: "Attribution", path: "/crm/attribution", icon: BarChart3 },
      { label: "Policy", path: "/crm/policy", icon: SlidersHorizontal },
      { label: "Referrals", path: "/crm/referrals", icon: UsersRound },
      { label: "Duplicates", path: "/crm/duplicates", icon: Copy },
      { label: "Team", path: "/crm/team-performance", icon: Trophy },
    ],
  },
  {
    label: "Services", path: "/services", icon: CalendarCheck,
    children: [
      { label: "Booking Widget", path: "/services/bookings/schedule", icon: CalendarCheck },
      { label: "Rebooking", path: "/services/bookings/rebooking", icon: BrainIcon },
      { label: "Reminders", path: "/services/bookings/reminders", icon: BellRing },
      { label: "Group Sessions", path: "/services/groups", icon: UsersRound },
      { label: "Capacity Optimizer", path: "/services/capacity-optimizer", icon: CalendarClock },
      { label: "Field Dispatch", path: "/services/dispatch", icon: Truck },
    ],
  },
  {
    label: "Email", path: "/email", icon: Mail,
    children: [
      { label: "Campaigns", path: "/email/campaigns", icon: Send },
      { label: "Lists", path: "/email/lists", icon: ListFilter },
      { label: "Sequences", path: "/email/sequences", icon: RefreshCw },
      { label: "Tracking", path: "/email/tracking", icon: MousePointerClick },
    ],
  },
  {
    label: "Pages", path: "/pages", icon: FileText,
    children: [
      { label: "Sites", path: "/pages/sites", icon: Globe },
      { label: "Theme", path: "/pages/theme", icon: Palette },
      { label: "Forms", path: "/pages/forms", icon: FormInput },
      { label: "Performance", path: "/pages/performance", icon: Gauge },
      { label: "Media", path: "/pages/media", icon: Image },
    ],
  },
  { label: "Webinars", path: "/webinars", icon: Video },
  {
    label: "Workflows", path: "/workflows", icon: Zap,
    children: [
      { label: "Automations", path: "/workflows/automations", icon: Bot },
      { label: "Logs", path: "/workflows/logs", icon: ScrollText },
    ],
  },
  { label: "AI", path: "/ai", icon: Brain },
  {
    label: "Communications", path: "/communications", icon: MessageCircle,
    children: [
      { label: "Journeys", path: "/communications/journeys", icon: Route },
      { label: "Email Builder", path: "/communications/email-builder", icon: Mail },
      { label: "DM Flows", path: "/communications/dm-flows", icon: MessageSquare },
      { label: "Broadcasts", path: "/communications/dm-broadcasts", icon: Send },
    ],
  },
  { label: "Analytics", path: "/analytics", icon: BarChart3 },
  { label: "Marketplace", path: "/marketplace", icon: Package },
  { label: "Affiliates", path: "/affiliate", icon: UsersRound },
  { label: "Reputation", path: "/reputation", icon: Star },
  { label: "Briefing", path: "/briefing", icon: Newspaper },
  { label: "Community", path: "/community", icon: UsersRound },
  { label: "Courses", path: "/courses", icon: GraduationCap },
];

const BOTTOM_ITEMS: NavItem[] = [
  { label: "Settings", path: "/settings", icon: Settings },
];

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

export function Sidebar({ isOpen, onClose }: SidebarProps) {
  const location = useLocation();
  const { persona } = usePersona();
  const { unreadCount } = useUnreadCount();
  const { overdueCount } = useOverdueTaskCount();
  const featureReveal = useFeatureReveal();
  const terminology = useTerminology();

  // Inject badge counts into static nav items
  const navItemsWithBadges = NAV_ITEMS.map((item) => {
    const label =
      item.path === "/crm/contacts"
        ? terminology.contacts
        : item.path === "/crm/deals"
          ? terminology.deals
          : item.path === "/crm/tasks"
            ? `${terminology.task}s`
            : item.label;

    const children = item.children?.map((child) => ({
      ...child,
      label:
        child.path === "/crm/contacts"
          ? terminology.contacts
          : child.path === "/crm/deals"
            ? terminology.deals
            : child.path === "/crm/tasks"
              ? `${terminology.task}s`
              : child.label,
    }));

    if (item.path === "/inbox" && unreadCount > 0) {
      return { ...item, label, children, badge: unreadCount };
    }
    if (item.path === "/crm" && overdueCount > 0 && item.children) {
      return {
        ...item,
        label,
        badge: overdueCount,
        children: (children ?? item.children).map((child) =>
          child.path === "/crm/tasks" ? { ...child, badge: overdueCount } : child,
        ),
      };
    }
    return { ...item, label, children };
  });

  const visibleNavItems = navItemsWithBadges
    .filter((item) => isNavVisibleForPersona(item.path, persona))
    .filter((item) => featureReveal.isRevealed(item.path));
  const visibleBottomItems = BOTTOM_ITEMS
    .filter((item) => isNavVisibleForPersona(item.path, persona))
    .filter((item) => featureReveal.isRevealed(item.path));

  return (
    <>
      {isOpen && (
        <div className="fixed inset-0 z-40 bg-black/50 lg:hidden" onClick={onClose} />
      )}
      <aside
        data-sidebar
        className={cn(
          "density-sidebar fixed inset-y-0 left-0 z-50 flex flex-col border-r border-border bg-card transition-transform duration-200 lg:static lg:translate-x-0",
          isOpen ? "translate-x-0" : "-translate-x-full",
        )}
      >
        <div className="border-b border-border p-4">
          <TenantSwitcher />
        </div>
        <nav className="flex-1 overflow-y-auto p-3">
          <ul className="space-y-1">
            {visibleNavItems.map((item) => (
              <NavGroup
                key={item.path}
                item={item}
                currentPath={location.pathname}
                isNew={featureReveal.isNew(item.path)}
                onDismissNewBadge={featureReveal.dismissBadge}
              />
            ))}
          </ul>
        </nav>
        <div className="border-t border-border p-3">
          <ul className="space-y-1">
            {visibleBottomItems.map((item) => (
              <NavGroup
                key={item.path}
                item={item}
                currentPath={location.pathname}
                isNew={featureReveal.isNew(item.path)}
                onDismissNewBadge={featureReveal.dismissBadge}
              />
            ))}
          </ul>
        </div>
      </aside>
    </>
  );
}

function NavGroup(
  {
    item,
    currentPath,
    isNew,
    onDismissNewBadge,
  }: {
    item: NavItem;
    currentPath: string;
    isNew: boolean;
    onDismissNewBadge: (path: string) => void;
  },
) {
  const isActive = currentPath === item.path || currentPath.startsWith(item.path + "/");
  const hasChildren = item.children && item.children.length > 0;
  const Icon = item.icon;
  const featureKey = normalizeFeatureUsageKey(item.path);

  return (
    <li>
      <NavLink
        to={item.path}
        end={!item.children}
        aria-current={isActive ? "page" : undefined}
        onClick={() => {
          trackFeatureUsage(featureKey);
          if (isNew) {
            onDismissNewBadge(item.path);
          }
        }}
        className={({ isActive: linkActive }) =>
          cn(
            "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-all duration-200",
            linkActive || (isActive && !hasChildren)
              ? "bg-[var(--color-primary-light)] text-[var(--color-primary)] border-l-[3px] border-l-[var(--color-primary)]"
              : "text-[var(--color-muted-foreground)] hover:bg-[var(--color-surface)] hover:text-[var(--color-foreground)] border-l-[3px] border-l-transparent",
          )
        }
      >
        <Icon className="h-4 w-4 flex-shrink-0" />
        <span>{item.label}</span>
        {isNew && (
          <span className="rounded-full bg-[var(--color-accent-action)] px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-[0.08em] text-white">
            New
          </span>
        )}
        {item.badge !== undefined && item.badge > 0 && (
          <span className="ml-auto rounded-full bg-[var(--color-primary)] px-2 py-0.5 text-xs text-white">
            {item.badge}
          </span>
        )}
      </NavLink>
      {hasChildren && isActive && (
        <ul className="ml-6 mt-1 space-y-1">
          {item.children!.map((child) => {
            const ChildIcon = child.icon;
            return (
              <li key={child.path}>
                <NavLink
                  to={child.path}
                  onClick={() => trackFeatureUsage(normalizeFeatureUsageKey(child.path))}
                  className={({ isActive: childActive }) =>
                    cn(
                      "flex items-center gap-2 rounded-md px-3 py-1.5 text-sm transition-colors",
                      childActive
                        ? "text-[var(--color-primary)] font-medium"
                        : "text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]",
                    )
                  }
                >
                  <ChildIcon className="h-3.5 w-3.5 flex-shrink-0" />
                  <span>{child.label}</span>
                  {child.badge !== undefined && child.badge > 0 && (
                    <span className="ml-auto rounded-full bg-[var(--color-accent-action)] px-1.5 py-0.5 text-[10px] text-white">
                      {child.badge}
                    </span>
                  )}
                </NavLink>
              </li>
            );
          })}
        </ul>
      )}
    </li>
  );
}
